import React, { useCallback } from 'react';
import { ActivityIndicator, Modal, StyleSheet, TouchableOpacity, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidV4 } from 'uuid';
import Text from '../global/Text';
import { RootState } from '../../store/reducers';
import { npsDismissAsync, npsSubmitAsync, npsCloseModal } from '../../store/reducers/nps/nps.action';
import theme from '../../constants/theme';
import fontMaker from '../../utils/font';
import { getCurrentUserIdentity } from '../../utils/userIdentity';
import NPSQuestionPager from './NPSQuestionPager';
import type { NpsResponseDetail } from './npsResponseDetails';
import { runAndClearNpsCallback } from './npsCallback';

import { selectProfile } from '../../store/selectors/profile';
const ORIGINATED_FROM = 'Web App';

const NPSModal = () => {
  const dispatch = useDispatch();
  const isModalOpen = useSelector((s: RootState) => s.npsUser.isModalOpen);
  const questionnaire = useSelector((s: RootState) => s.npsUser.questionnaire);
  const isSubmitting = useSelector((s: RootState) => s.npsUser.isSubmitting);
  const isDismissing = useSelector((s: RootState) => s.npsUser.isDismissing);
  const triggerSource = useSelector((s: RootState) => s.npsUser.triggerSource);
  const startDateTime = useSelector((s: RootState) => s.npsUser.startDateTime);
  const profile = useSelector((s: RootState) => selectProfile(s) as any);
  const auth = useSelector((s: RootState) => s.firebase.auth);
  const { userId, organizationId } = getCurrentUserIdentity(profile, auth);

  const runCallback = useCallback(() => {
    runAndClearNpsCallback();
  }, []);

  const handleDismiss = useCallback(() => {
    if (isDismissing || isSubmitting) return;
    dispatch(
      npsDismissAsync.request({
        respondentId: userId,
        organizationID: organizationId,
        actionType: 'dismissed',
        deviceDetails: {
          osType: 'Web',
          osVersion: navigator.userAgent,
          deviceId: userId,
          browserType: navigator.userAgent,
          ipAddress: '',
          softwareVersion: '1.0.0'
        }
      })
    );
    dispatch(npsCloseModal());
    runCallback();
  }, [dispatch, isDismissing, isSubmitting, organizationId, runCallback, userId]);

  const handleSubmit = useCallback(
    (responseDetails: NpsResponseDetail[]) => {
      if (!questionnaire || !profile) return;

      dispatch(
        npsSubmitAsync.request({
          npsReportId: uuidV4(),
          submittedDate: new Date().toISOString(),
          respondentId: userId,
          respondentEmail: profile.email ?? '',
          respondentRole: profile.role ?? '',
          organizationID: organizationId,
          originatedFrom: ORIGINATED_FROM,
          gtmTracking: triggerSource ?? 'Report Submission (Single Audit)',
          deviceDetails: {
            osType: 'Web',
            osVersion: navigator.userAgent,
            deviceId: userId,
            browserType: navigator.userAgent,
            ipAddress: '',
            softwareVersion: '1.0.0'
          },
          questionnaireId: questionnaire.questionnaireID,
          responseDetails,
          startDate: startDateTime ?? new Date().toISOString(),
          actionType: 'submitted'
        })
      );
      runCallback();
    },
    [dispatch, organizationId, profile, questionnaire, runCallback, startDateTime, triggerSource, userId]
  );

  if (!isModalOpen || !questionnaire) return null;

  const isLoading = isDismissing || isSubmitting;

  return (
    <Modal visible={isModalOpen} transparent animationType="fade" statusBarTranslucent onRequestClose={handleDismiss}>
      <View style={styles.overlay}>
        <View style={styles.card}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.brandText}>Nimbly</Text>
            {isLoading ? (
              <ActivityIndicator color={theme.colors.primary} size="small" />
            ) : (
              <TouchableOpacity
                onPress={handleDismiss}
                style={styles.closeButton}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <Text style={styles.closeIcon}>✕</Text>
              </TouchableOpacity>
            )}
          </View>

          {/* Question pager */}
          <View style={styles.content}>
            <NPSQuestionPager
              questionnaire={questionnaire}
              isSubmitting={isSubmitting}
              onSubmit={handleSubmit}
              onDismiss={handleDismiss}
            />
          </View>

          {/* Full-screen loading overlay during dismiss/submit */}
          {isLoading && (
            <View style={styles.loadingOverlay}>
              <ActivityIndicator color={theme.colors.primary} size="large" />
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: 16,
    width: '100%',
    maxWidth: 480,
    maxHeight: '85%',
    overflow: 'hidden',
    elevation: 10,
    shadowColor: '#000',
    shadowOpacity: 0.15,
    shadowRadius: 20,
    shadowOffset: { width: 0, height: 4 }
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0'
  },
  brandText: {
    flex: 1,
    ...fontMaker({ weight: 'Bold' }),
    fontSize: 18,
    color: theme.colors.primary
  },
  closeButton: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 16,
    backgroundColor: '#f5f5f5'
  },
  closeIcon: {
    fontSize: 16,
    color: theme.colors.darkgray,
    lineHeight: 18
  },
  content: { flex: 1, minHeight: 300 },
  loadingOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(255,255,255,0.7)',
    alignItems: 'center',
    justifyContent: 'center'
  }
});

export default NPSModal;
